import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { testimonialService } from '../services/api';

const useQuery = () => new URLSearchParams(useLocation().search);

const TestimonialSubmit: React.FC = () => {
  const query = useQuery();
  const token = query.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);

  const formatDisplayName = (name?: string) => {
    if (!name) return 'Program Alumni';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'Program Alumni';
    if (parts.length === 1) return parts[0];
    const first = parts[0];
    const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || '';
    return lastInitial ? `${first} ${lastInitial}.` : first;
  };

  const [formData, setFormData] = useState({
    name: '',
    role_title: '',
    quote: '',
    story: '',
    countries: '',
    current_location: '',
    consent_publish: false,
    consent_photo: false,
    photo_url: '',
  });

  const parsedCountries = useMemo(() => (
    formData.countries
      .split(/[,\n]/g)
      .map((item) => item.trim())
      .filter(Boolean)
  ), [formData.countries]);

  useEffect(() => {
    if (!token) {
      setError('Missing testimonial link token.');
      setLoading(false);
      return;
    }
    let active = true;
    testimonialService.validateRequest(token)
      .then(() => {
        if (!active) return;
        setValid(true);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.error || 'This testimonial link is invalid or has expired.');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    let nextValue: string | boolean = value;
    if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
      nextValue = event.target.checked;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!formData.consent_publish) {
      setError('Please confirm you allow us to publish your testimonial.');
      return;
    }
    try {
      setLoading(true);
      await testimonialService.submit({
        token,
        name: formData.name.trim() || undefined,
        role_title: formData.role_title.trim() || undefined,
        quote: formData.quote.trim(),
        story: formData.story.trim() || undefined,
        countries: parsedCountries,
        current_location: formData.current_location.trim() || undefined,
        consent_publish: formData.consent_publish,
        consent_photo: formData.consent_photo,
        photo_url: formData.photo_url.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to submit your testimonial right now.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    if (!file || !token) return;
    setPhotoUploadError(null);
    setPhotoUploading(true);
    const localPreview = URL.createObjectURL(file);
    setPhotoPreviewUrl(localPreview);
    try {
      const payload = await testimonialService.getPhotoUploadUrl({
        token,
        filename: file.name,
        content_type: file.type || 'image/jpeg',
      });
      await fetch(payload.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
        },
        body: file,
      });
      setFormData((prev) => ({ ...prev, photo_url: payload.key }));
    } catch (err: any) {
      setPhotoUploadError(err?.response?.data?.error || err?.message || 'Unable to upload photo right now.');
      setPhotoPreviewUrl(null);
      setFormData((prev) => ({ ...prev, photo_url: '' }));
    } finally {
      setPhotoUploading(false);
    }
  };

  if (loading && !submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  if (error && !valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 text-slate-100">
        <div className="max-w-md w-full rounded-2xl border border-slate-800 bg-slate-900/70 p-6 text-center">
          <h1 className="text-lg font-semibold text-white">Testimonial Link</h1>
          <p className="mt-2 text-sm text-slate-300">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const previewName = formatDisplayName(formData.name || undefined);
    const previewRole = formData.role_title.trim() || 'Alumni';
    const previewQuote = formData.quote.trim();
    const previewStory = formData.story.trim();
    const previewCountries = parsedCountries;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 text-slate-100">
        <div className="max-w-2xl w-full rounded-3xl border border-emerald-400/40 bg-emerald-500/10 p-8 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-emerald-100">Thank you for sharing!</h1>
            <p className="mt-3 text-sm text-emerald-200">
              Your testimonial has been received. We appreciate your time and support.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
            <div className="flex items-start gap-4">
              {photoPreviewUrl ? (
                <img
                  src={photoPreviewUrl}
                  alt={previewName}
                  className="h-14 w-14 rounded-full object-cover border border-slate-700"
                />
              ) : (
                <div className="h-14 w-14 rounded-full bg-emerald-500/20 text-emerald-100 flex items-center justify-center text-sm font-semibold">
                  {previewName.split(' ').map((part) => part[0]).join('').slice(0, 2)}
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Preview</p>
                <p className="text-sm font-semibold text-white">{previewName}</p>
                <p className="text-xs text-slate-400">{previewRole}</p>
              </div>
            </div>

            <div className="mt-4 space-y-3 text-sm text-slate-200">
              {previewQuote ? <p className="text-base text-slate-100">“{previewQuote}”</p> : null}
              {previewStory ? <p>{previewStory}</p> : null}
              {previewCountries.length ? (
                <div className="flex flex-wrap gap-2">
                  {previewCountries.map((country) => (
                    <span
                      key={country}
                      className="inline-flex items-center rounded-full border border-emerald-200/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-100"
                    >
                      {country}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
      <div className="max-w-2xl mx-auto rounded-3xl border border-slate-800 bg-slate-900/70 p-8 shadow-sm">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.35em] text-emerald-300">Alumni Testimonial</p>
          <h1 className="text-2xl font-semibold text-white mt-2">Share Your Experience</h1>
          <p className="text-sm text-slate-300 mt-2">
            Your story helps future students make confident decisions. Thank you for taking a few minutes to share.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {error}
          </div>
        )}

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-400">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-400">Role / Profession</label>
              <input
                type="text"
                name="role_title"
                value={formData.role_title}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-slate-400">Short Quote *</label>
            <textarea
              name="quote"
              value={formData.quote}
              onChange={handleChange}
              rows={3}
              required
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-slate-400">Full Testimonial</label>
            <textarea
              name="story"
              value={formData.story}
              onChange={handleChange}
              rows={4}
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-slate-400">Countries Visited</label>
            <textarea
              name="countries"
              value={formData.countries}
              onChange={handleChange}
              rows={2}
              placeholder="Malaysia, Indonesia, Qatar"
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-slate-400">Current Location</label>
            <input
              type="text"
              name="current_location"
              value={formData.current_location}
              onChange={handleChange}
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-slate-400">Photo (optional)</label>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  handlePhotoUpload(file);
                }
              }}
              className="mt-2 block w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-slate-900 hover:file:bg-emerald-400 border border-slate-700 rounded-lg"
            />
            <div className="mt-2 text-xs text-slate-500">JPG or PNG, max 5 MB.</div>
            {photoUploading && <div className="text-sm text-emerald-300 mt-2">Uploading photo...</div>}
            {photoUploadError && <div className="text-sm text-rose-300 mt-2">{photoUploadError}</div>}
            {photoPreviewUrl && (
              <img
                src={photoPreviewUrl}
                alt="Upload preview"
                className="mt-3 h-28 w-28 rounded-xl object-cover border border-slate-700"
              />
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                name="consent_publish"
                checked={formData.consent_publish}
                onChange={handleChange}
                className="mt-1 accent-emerald-400"
                required
              />
              I allow Muslim Study Abroad to publish my testimonial.
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                name="consent_photo"
                checked={formData.consent_photo}
                onChange={handleChange}
                className="mt-1 accent-emerald-400"
              />
              I allow my photo to be displayed with my testimonial.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-emerald-400 disabled:opacity-60"
          >
            {loading ? 'Submitting…' : 'Submit Testimonial'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TestimonialSubmit;
